import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const LEBT_COMMERCIAL_PAGE_ID = 'lebt-commercial';

export type Tab = 'orders' | 'business';
export const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'orders', label: 'LEBT - Orders' },
  { value: 'business', label: 'LEBT - Business' },
];
export type Comparison = 'PP' | 'LY' | 'Custom';
export const COMPARISONS: ReadonlyArray<{ value: Comparison; label: string }> = [
  { value: 'PP', label: 'Previous Period (Same Day Count)' },
  { value: 'LY', label: 'Last Year (Same Dates)' },
  { value: 'Custom', label: 'Custom' },
];
export type Cadence = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly';
export const CADENCES: ReadonlyArray<Cadence> = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];
export type Metric = 'Purchasers' | 'Orders' | 'Gross Sales' | 'Margin';
export const METRICS: ReadonlyArray<Metric> = ['Purchasers', 'Orders', 'Gross Sales', 'Margin'];
export type Split = 'All' | 'Business Name' | 'Product Bk';
export const SPLITS: ReadonlyArray<Split> = ['All', 'Business Name', 'Product Bk'];

/** Filters declared in svc-reporting's native-specs/lebt-commercial.json, keyed by filter id. */
export const FILTER_DEFINITIONS = [
  { key: 'business_name', label: 'Business name' },
  { key: 'industry', label: 'Industry' },
  { key: 'employee_count', label: 'Employee count' },
  { key: 'product', label: 'Product' },
  { key: 'product_bk', label: 'Product bk' },
  { key: 'order_id', label: 'Order' },
  { key: 'business_type', label: 'Business type' },
] as const;
export type FilterKey = (typeof FILTER_DEFINITIONS)[number]['key'];

/** The workbook's saved Product filter: no gift cards, purchased credit or unmatched items. */
const DEFAULT_PRODUCTS: ReadonlyArray<string> = [
  'Car Hire',
  'Custom Offer - Flight',
  'Custom Offer - Hotel',
  'Flight',
  'Limited Time Lux Exclusive',
  'Limited Time Lux Partner',
  'Lux Partner Property',
  'Lux Premium Collection',
  'Merchant Fee',
  'Offline Flight',
  'Service Fee',
];

export type LebtFilters = {
  tab: Tab;
  from: string;
  to: string;
  comparison: Comparison;
  compareFrom: string;
  compareTo: string;
  cadence: Cadence;
  metric: Metric;
  split: Split;
  selections: Record<FilterKey, Array<string>>;
};

// Workspace links strip a bare `metric` parameter (nativePageParams), so the chart metric uses its own name.
const METRIC_PARAM = 'chartMetric';
const DAY = 86_400_000;
const isDay = (value: string | null): value is string => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));
const toTime = (day: string) => Date.parse(`${day}T00:00:00Z`);
const toDay = (time: number) => new Date(time).toISOString().slice(0, 10);
const addDays = (day: string, days: number) => toDay(toTime(day) + days * DAY);
const daysBetween = (from: string, to: string) => Math.round((toTime(to) - toTime(from)) / DAY);
export function addYears(day: string, years: number): string {
  const date = new Date(toTime(day));
  date.setUTCFullYear(date.getUTCFullYear() + years);
  // 29 Feb moves to 28 Feb rather than rolling into March.
  return date.getUTCDate() === Number(day.slice(8))
    ? toDay(date.getTime())
    : toDay(date.getTime() - date.getUTCDate() * DAY);
}

export function sydneyToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(now);
}

export function defaultFilters(today: string = sydneyToday()): LebtFilters {
  // The workbook opens on the 30 days to its last full day of data (P. Start Date = max - 29).
  const to = addDays(today, -1);
  return {
    tab: 'orders',
    from: addDays(to, -29),
    to,
    comparison: 'PP',
    compareFrom: addYears(addDays(to, -29), -1),
    compareTo: addYears(to, -1),
    cadence: 'Weekly',
    metric: 'Gross Sales',
    split: 'All',
    selections: {
      business_name: [],
      industry: [],
      employee_count: [],
      product: [...DEFAULT_PRODUCTS],
      product_bk: [],
      order_id: [],
      business_type: [],
    },
  };
}

const pick = <T extends string>(value: string | null, allowed: ReadonlyArray<T>, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

export function parseFilters(params: URLSearchParams, today?: string): LebtFilters {
  const defaults = defaultFilters(today);
  const day = (key: string, fallback: string) => {
    const value = params.get(key);
    return isDay(value) ? value : fallback;
  };
  const selections = { ...defaults.selections };
  for (const { key } of FILTER_DEFINITIONS) {
    const raw = params.get(key);
    if (raw === null) {
      continue;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        selections[key] = parsed.filter((value): value is string => typeof value === 'string');
      }
    } catch {
      // A hand-edited value falls back to the default selection.
    }
  }
  return {
    tab: pick(
      params.get('tab'),
      TABS.map((tab) => tab.value),
      defaults.tab,
    ),
    from: day('from', defaults.from),
    to: day('to', defaults.to),
    comparison: pick(
      params.get('comparison'),
      COMPARISONS.map((item) => item.value),
      defaults.comparison,
    ),
    compareFrom: day('compareFrom', defaults.compareFrom),
    compareTo: day('compareTo', defaults.compareTo),
    cadence: pick(params.get('cadence'), CADENCES, defaults.cadence),
    metric: pick(params.get(METRIC_PARAM), METRICS, defaults.metric),
    split: pick(params.get('split'), SPLITS, defaults.split),
    selections,
  };
}

export function writeFilters(filters: LebtFilters, params: URLSearchParams, today?: string): URLSearchParams {
  const defaults = defaultFilters(today);
  const next = new URLSearchParams(params);
  for (const key of [
    'tab',
    'from',
    'to',
    'comparison',
    'compareFrom',
    'compareTo',
    'cadence',
    'metric',
    'split',
  ] as const) {
    const param = key === 'metric' ? METRIC_PARAM : key;
    if (filters[key] === defaults[key]) {
      next.delete(param);
    } else {
      next.set(param, filters[key]);
    }
  }
  for (const { key } of FILTER_DEFINITIONS) {
    const values = [...new Set(filters.selections[key])].sort();
    if (JSON.stringify(values) === JSON.stringify([...defaults.selections[key]].sort())) {
      next.delete(key);
    } else {
      next.set(key, JSON.stringify(values));
    }
  }
  return next;
}

export function filtersError(filters: LebtFilters): string | null {
  if (filters.from > filters.to) {
    return 'The start date is after the end date.';
  }
  if (filters.comparison === 'Custom' && filters.compareFrom > filters.compareTo) {
    return 'The comparison start date is after its end date.';
  }
  return null;
}

/**
 * The workbook's "P. Comparison Logic": the same number of days just before the window, the same dates
 * a year earlier, or the custom comparison dates.
 */
export function comparisonWindow(filters: LebtFilters): { from: string; to: string } {
  if (filters.comparison === 'LY') {
    return { from: addYears(filters.from, -1), to: addYears(filters.to, -1) };
  }
  if (filters.comparison === 'Custom') {
    return { from: filters.compareFrom, to: filters.compareTo };
  }
  const days = daysBetween(filters.from, filters.to) + 1;
  return { from: addDays(filters.from, -days), to: addDays(filters.from, -1) };
}

/**
 * The Lens request. An empty selection means every value; Product has a saved default, so clearing it
 * sends every listed product rather than falling back to that default.
 */
export function lensFilters(filters: LebtFilters, options: Record<string, Array<string>> = {}): LensFilterValues {
  const values: LensFilterValues = {
    period: { window: { from: filters.from, to: filters.to }, comparison: comparisonWindow(filters) },
  };
  for (const { key } of FILTER_DEFINITIONS) {
    const selected = filters.selections[key];
    if (selected.length) {
      values[key] = selected;
    } else if (key === 'product' && options.product?.length) {
      values[key] = options.product;
    }
  }
  return values;
}

export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
export type CardProblem = { problem: string };
export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  if (card.status === 'restricted') {
    return { problem: card.message ?? 'You do not have access to this section.' };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};

function parseKey<T>(value: unknown): T | null {
  try {
    return typeof value === 'string' ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

export type Kpis = {
  purchasers: number | null;
  orders: number | null;
  grossSales: number | null;
  margin: number | null;
  grossSalesInclCredit: number | null;
};

const kpisOf = (row: Record<string, unknown> | undefined): Kpis => ({
  purchasers: num(row?.purchasers),
  orders: num(row?.orders),
  grossSales: num(row?.gross_sales_aud),
  margin: num(row?.margin_aud),
  grossSalesInclCredit: num(row?.gross_sales_incl_credit_aud),
});

export function kpiPair(result: CardResult): { current: Kpis; previous: Kpis | null } {
  const comparison = result.card.comparison;
  return {
    current: kpisOf(result.rows[0]),
    previous:
      comparison && (comparison.status === 'ready' || comparison.status === 'empty')
        ? kpisOf(comparison.rows?.[0])
        : null,
  };
}

export const percentChange = (current: number | null, previous: number | null) =>
  current === null || previous === null || previous === 0 ? null : current / previous - 1;

export type OrderLine = {
  date: string;
  order: string;
  business: string;
  businessName: string;
  category: string;
  product: string;
  productBk: string;
  status: string;
  grossSales: number;
  margin: number;
  grossSalesInclCredit: number;
  giftCard: number;
  purchasedCredit: number;
  items: number;
};

export function orderLines(result: CardResult): Array<OrderLine> {
  return result.rows.flatMap((row) => {
    const key = parseKey<Record<string, string>>(row.line_key);
    if (!key) {
      return [];
    }
    return [
      {
        date: key.d,
        order: key.o,
        business: key.b,
        businessName: key.n ?? 'Unknown',
        category: key.c ?? 'Unknown',
        product: key.p ?? 'Unknown',
        productBk: key.k ?? 'Unknown',
        status: key.s ?? 'Unknown',
        grossSales: num(row.gross_sales_aud) ?? 0,
        margin: num(row.margin_aud) ?? 0,
        grossSalesInclCredit: num(row.gross_sales_incl_credit_aud) ?? 0,
        giftCard: num(row.gift_card_aud) ?? 0,
        purchasedCredit: num(row.purchased_credit_aud) ?? 0,
        items: num(row.order_items) ?? 0,
      },
    ];
  });
}

export type ChartLine = {
  date: string;
  order: string;
  business: string;
  businessName: string;
  productBk: string;
  grossSales: number;
  margin: number;
};

export function chartLines(result: CardResult): Array<ChartLine> {
  return result.rows.flatMap((row) => {
    const key = parseKey<Record<string, string>>(row.chart_key);
    return key
      ? [
          {
            date: key.d,
            order: key.o,
            business: key.b,
            businessName: key.n ?? 'Unknown',
            productBk: key.p ?? 'Unknown',
            grossSales: num(row.gross_sales_aud) ?? 0,
            margin: num(row.margin_aud) ?? 0,
          },
        ]
      : [];
  });
}

/** The workbook's "Date | Cadence": the first day of the day, Monday week, month, quarter or year. */
export function bucketOf(day: string, cadence: Cadence): string {
  if (cadence === 'Daily') {
    return day;
  }
  if (cadence === 'Weekly') {
    const weekday = (new Date(toTime(day)).getUTCDay() + 6) % 7;
    return addDays(day, -weekday);
  }
  const month = Number(day.slice(5, 7));
  if (cadence === 'Monthly') {
    return `${day.slice(0, 7)}-01`;
  }
  if (cadence === 'Quarterly') {
    return `${day.slice(0, 4)}-${String(month - ((month - 1) % 3)).padStart(2, '0')}-01`;
  }
  return `${day.slice(0, 4)}-01-01`;
}

export function bucketEnd(start: string, cadence: Cadence): string {
  if (cadence === 'Daily') {
    return start;
  }
  if (cadence === 'Weekly') {
    return addDays(start, 6);
  }
  const months = cadence === 'Monthly' ? 1 : cadence === 'Quarterly' ? 3 : 12;
  const date = new Date(toTime(start));
  date.setUTCMonth(date.getUTCMonth() + months);
  return toDay(date.getTime() - DAY);
}

type Measurable = { order: string; business: string; grossSales: number; margin: number };

/** The workbook's "Chart Metric": sums for money, distinct orders or businesses for counts. */
function measure<T extends Measurable>(lines: ReadonlyArray<T>, metric: Metric, gross: (line: T) => number): number {
  if (metric === 'Orders') {
    return new Set(lines.map((line) => line.order)).size;
  }
  if (metric === 'Purchasers') {
    return new Set(lines.map((line) => line.business)).size;
  }
  return lines.reduce((total, line) => total + (metric === 'Margin' ? line.margin : gross(line)), 0);
}

const MAX_SERIES = 10;

type TrendSeries = { name: string; values: Array<number | null> };
export type Trend = { buckets: Array<string>; series: Array<TrendSeries> };

/** Chart for Orders Dash: the chosen metric by cadence, split by business or product bk when asked. */
export function trendOf(lines: ReadonlyArray<ChartLine>, cadence: Cadence, metric: Metric, split: Split): Trend {
  const buckets = [...new Set(lines.map((line) => bucketOf(line.date, cadence)))].sort();
  const seriesKey = (line: ChartLine) =>
    split === 'Business Name' ? line.businessName : split === 'Product Bk' ? line.productBk : 'All';
  const totals = new Map<string, number>();
  for (const line of lines) {
    totals.set(seriesKey(line), (totals.get(seriesKey(line)) ?? 0) + Math.abs(line.grossSales));
  }
  const ranked = [...totals.keys()].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));
  const kept = new Set(ranked.slice(0, MAX_SERIES));
  const name = (line: ChartLine) => (kept.has(seriesKey(line)) ? seriesKey(line) : 'Other');
  const names = [...ranked.slice(0, MAX_SERIES), ...(ranked.length > MAX_SERIES ? ['Other'] : [])];
  return {
    buckets,
    series: names.map((seriesName) => ({
      name: seriesName,
      values: buckets.map((bucket) => {
        const matching = lines.filter((line) => name(line) === seriesName && bucketOf(line.date, cadence) === bucket);
        return matching.length ? measure(matching, metric, (line) => line.grossSales) : null;
      }),
    })),
  };
}

type SummaryRow = { business: string; order: string; values: Record<string, number>; total: number };

/**
 * Orders Summary: business and order down, Category across, the "Selected Metric" in each cell. Its
 * gross sales is the workbook's Gross Sales incl GC and PC, not certified TTV.
 */
export function ordersSummary(
  lines: ReadonlyArray<OrderLine>,
  metric: Metric,
): { categories: Array<string>; rows: Array<SummaryRow> } {
  const categories = [...new Set(lines.map((line) => line.category))].sort();
  const groups = new Map<string, Array<OrderLine>>();
  for (const line of lines) {
    const key = JSON.stringify([line.businessName, line.order]);
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }
  const rows = [...groups.entries()].map(([key, group]) => {
    const [business, order] = JSON.parse(key) as [string, string];
    const values: Record<string, number> = {};
    for (const category of categories) {
      const matching = group.filter((line) => line.category === category);
      if (matching.length) {
        values[category] = measure(matching, metric, (line) => line.grossSalesInclCredit);
      }
    }
    return { business, order, values, total: measure(group, metric, (line) => line.grossSalesInclCredit) };
  });
  rows.sort((a, b) => a.business.localeCompare(b.business) || b.total - a.total);
  return { categories, rows };
}

type WeekColumn = {
  week: string;
  grossSalesInclCredit: number;
  margin: number;
  orders: number;
  purchasers: number;
};

export function weeklySummary(lines: ReadonlyArray<OrderLine>): Array<WeekColumn> {
  const weeks = [...new Set(lines.map((line) => bucketOf(line.date, 'Weekly')))].sort();
  return weeks.map((week) => {
    const matching = lines.filter((line) => bucketOf(line.date, 'Weekly') === week);
    return {
      week,
      grossSalesInclCredit: measure(matching, 'Gross Sales', (line) => line.grossSalesInclCredit),
      margin: measure(matching, 'Margin', (line) => line.grossSalesInclCredit),
      orders: measure(matching, 'Orders', (line) => line.grossSalesInclCredit),
      purchasers: measure(matching, 'Purchasers', (line) => line.grossSalesInclCredit),
    };
  });
}

export type Business = {
  id: string;
  name: string;
  joined: string | null;
  lastTransaction: string | null;
  activity: string;
  industry: string;
  onboarding: string;
  asOf: string | null;
  orders: number;
};

export function businessesOf(result: CardResult): Array<Business> {
  return result.rows.flatMap((row) => {
    const key = parseKey<Record<string, string | null>>(row.business_key);
    return key?.id
      ? [
          {
            id: key.id,
            name: key.n ?? 'Unknown',
            joined: key.j ?? null,
            lastTransaction: key.t ?? null,
            activity: key.a ?? 'Unknown',
            industry: key.i ?? 'Unknown',
            onboarding: key.s ?? 'Unknown',
            asOf: key.asof ?? null,
            orders: num(row.orders_lifetime) ?? 0,
          },
        ]
      : [];
  });
}

/** The workbook's L7D, L30D, MTD and YTD Registrations, counted against today as Tableau's TODAY() does. */
export function registrations(businesses: ReadonlyArray<Business>, today: string) {
  const joined = businesses.flatMap((business) => (business.joined ? [business.joined] : []));
  const within = (days: number) => joined.filter((day) => day <= today && daysBetween(day, today) <= days).length;
  return {
    l7d: within(7),
    l30d: within(30),
    mtd: joined.filter((day) => day.slice(0, 7) === today.slice(0, 7)).length,
    ytd: joined.filter((day) => day.slice(0, 4) === today.slice(0, 4)).length,
  };
}

export function registrationsByMonth(businesses: ReadonlyArray<Business>): Array<{ month: string; count: number }> {
  const counts = new Map<string, number>();
  for (const business of businesses) {
    if (business.joined) {
      const month = business.joined.slice(0, 7);
      counts.set(month, (counts.get(month) ?? 0) + 1);
    }
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => ({ month, count }));
}

/** The industries saved on the workbook's Accounts Summary and Activity Chart; newer ones (mining, utilities) are not on it. */
export const SAVED_INDUSTRIES = [
  'accommodation',
  'administration',
  'agriculture',
  'arts',
  'construction',
  'education',
  'finance',
  'health',
  'manufacturing',
  'other',
  'realestate',
  'retail',
  'science',
  'telecommunications',
  'transport',
];

export const ACTIVITY_ORDER = [
  'Active L30',
  'Active L60',
  'Searching L60',
  'Inactive - Last Booking 61-180',
  'New YTD - No Bookings',
  'Inactive - No Bookings',
  'Inactive - Last Booking 180+',
];

export function activityCounts(businesses: ReadonlyArray<Business>): Array<{ status: string; count: number }> {
  return ACTIVITY_ORDER.map((status) => ({
    status,
    count: businesses.filter((business) => business.activity === status).length,
  })).filter((entry) => entry.count > 0);
}

export const adminBusinessPath = (id: string) => `/lebusiness/${encodeURIComponent(id)}/business`;
